import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { apiFetch, redirectToLogin } from "api/Api";

// Recharts components
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#FFBB28", "#0088FE", "#00C49F", "#FF8042", "#8884d8"];

const renderSmallPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name, value }) => {
  if (!value || percent === 0) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#334155"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      style={{ fontSize: "10.5px", fontWeight: "700" }}
    >
      {`${name}: ${value}`}
    </text>
  );
};

function getStatusChipColor(status) {
  const s = (status || "").toLowerCase();
  if (s === "resolved") return "success";
  if (s === "pending") return "warning";
  if (s === "in progress" || s === "inprogress") return "info";
  if (s === "rejected") return "error";
  return "default";
}

function SuperAdminDashboard() {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const res = await apiFetch("super_admin/get_dashboard_stats");
        if (res.success) {
          setStatsData(res.data);
        } else {
          const msg = (res.message || "").toLowerCase();
          if (msg.includes("invalid token") || msg.includes("token expired") || msg.includes("login again")) {
            redirectToLogin();
            return;
          }
          setError(res.message || "Failed to load dashboard data");
        }
      } catch (err) {
        console.error("Dashboard error:", err);
        const errMsg = (err.message || "").toLowerCase();
        if (
          errMsg.includes("invalid token") ||
          errMsg.includes("token expired") ||
          errMsg.includes("session expired") ||
          errMsg.includes("jwt expired") ||
          errMsg.includes("login again")
        ) {
          redirectToLogin();
          return;
        }
        setError(err.message || "Failed to connect to server");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={6} textAlign="center">
          <MDTypography variant="h5" color="text">
            Loading Dashboard Statistics...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !statsData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={6} textAlign="center">
          <MDTypography variant="h5" color="error">
            {error || "Unable to fetch stats"}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const { complaints, users, system, statusChart, deptChart, recentComplaints } = statsData;

  const totalComplaints = complaints?.total || 0;
  const resolvedComplaints = complaints?.resolved || 0;
  const pendingComplaints = complaints?.pending || 0;
  const inProgressComplaints = complaints?.in_progress || 0;
  const resolvedPercentage = totalComplaints > 0 ? Math.round((resolvedComplaints / totalComplaints) * 100) : 0;

  const userRoleChartData = [
    { name: "Leaders", value: users?.leaders || 0 },
    { name: "Workers", value: users?.workers || 0 },
    { name: "Citizens", value: users?.citizens || 0 },
    { name: "Admins", value: users?.admins || 0 },
  ].filter((item) => item.value > 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Welcome Header Banner */}
        <MDBox mb={3.5}>
          <Card
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
              color: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(26, 115, 232, 0.25)",
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <MDBox>
                <MDTypography variant="h4" sx={{ color: "#ffffff", fontWeight: 700, mb: 0.5 }}>
                  नागरिक सेवा मंच - Admin Overview
                </MDTypography>
                <MDTypography variant="button" sx={{ color: "rgba(255, 255, 255, 0.9)", display: "block", fontSize: "0.88rem" }}>
                  Real-time analytics, citizen complaint tracking & system administration
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1.5} alignItems="center">
                <Chip
                  label={`${resolvedPercentage}% Resolution Rate`}
                  sx={{ backgroundColor: "rgba(255,255,255,0.25)", color: "#ffffff", fontWeight: "bold", fontSize: "0.85rem" }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        {/* Row 1: Equal-Height Summary Statistics Cards */}
        <Grid container spacing={3} alignItems="stretch" mb={4}>
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="dark"
                icon="assignment"
                title="Total Complaints"
                count={totalComplaints}
                percentage={{
                  color: "success",
                  amount: `${resolvedPercentage}%`,
                  label: "Resolved",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="warning"
                icon="pending_actions"
                title="Pending"
                count={pendingComplaints}
                percentage={{
                  color: "warning",
                  amount: `${totalComplaints > 0 ? Math.round((pendingComplaints / totalComplaints) * 100) : 0}%`,
                  label: "of total",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="success"
                icon="check_circle"
                title="Resolved"
                count={resolvedComplaints}
                percentage={{
                  color: "success",
                  amount: `${resolvedPercentage}%`,
                  label: "Completed",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="info"
                icon="people"
                title="Total Users"
                count={users?.total || 0}
                percentage={{
                  color: "info",
                  amount: `${users?.leaders || 0} Leaders`,
                  label: `& ${users?.workers || 0} Workers`,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4} sx={{ display: "flex", flexDirection: "column" }}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="primary"
                icon="event"
                title="Janata Darbars"
                count={system?.janata_darbar || 0}
                percentage={{
                  color: "primary",
                  amount: `${system?.schemes || 0} Schemes`,
                  label: `Active`,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Row 2: Visual Charts (Complaints Status & Department Breakdown) */}
        <Grid container spacing={3} mb={3}>
          {/* Complaints Status Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", p: 2.5 }}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Complaints Status
                </MDTypography>
              </MDBox>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 15, right: 35, left: 35, bottom: 15 }}>
                  <Pie
                    data={statusChart && statusChart.length > 0 ? statusChart : [{ name: "No Data", value: 1 }]}
                    cx="50%"
                    cy="45%"
                    outerRadius={68}
                    dataKey="value"
                    label={renderSmallPieLabel}
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {(statusChart || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Complaints by Department Bar Chart */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", p: 2.5 }}>
              <MDBox mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Complaints by Department
                </MDTypography>
              </MDBox>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={deptChart || []} margin={{ top: 15, right: 25, left: 0, bottom: 40 }} maxBarSize={60}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#334155", fontWeight: 500 }} interval={0} dy={8} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#334155" }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#0088FE" name="Total Complaints" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        </Grid>

        {/* Row 3: Recent Complaints Table */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="medium">
                  Recent Complaints
                </MDTypography>
              </MDBox>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentComplaints && recentComplaints.length > 0 ? (
                      recentComplaints.map((item) => (
                        <TableRow key={item.complaint_id}>
                          <TableCell>
                            <MDTypography variant="button" fontWeight="medium">
                              {item.title}
                            </MDTypography>
                          </TableCell>
                          <TableCell>{item.category_name || "General"}</TableCell>
                          <TableCell>{item.location || "N/A"}</TableCell>
                          <TableCell>{item.priority || "Medium"}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.status || "Pending"}
                              color={getStatusChipColor(item.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <MDTypography variant="button" color="text">
                            No recent complaints found.
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SuperAdminDashboard;
